import React, { memo, useState } from 'react';
import { useSupplierQuery } from 'api';
import { Col, DatePicker, Form, FormModal, Row, Select, Upload } from 'components';
import { UploadContent } from 'components/common';
import { CalendarIcon } from 'components/common/icons';
import { MAX_FILE_SIZE } from 'constants/index';
import { ValidationErrors } from 'constants/validations';
import { useAddEdit } from './useAddEdit';

const { Dragger } = Upload;

interface IAddModal {
  openModal: boolean;
  onModalStateChange: () => void;
}

const ACCEPTED_FORMATS = ['text/csv', 'application/pdf'];

export const AddModal = memo((props: IAddModal) => {
  const { openModal, onModalStateChange } = props;
  const [isSaveBtnDisabled, setIsSaveBtnDisabled] = useState(true);
  const { data = {} } = useSupplierQuery({ page: 1, size: 100 });
  const { content = [] } = data;
  const { contextHolder, form, handleSubmit, isLoading } = useAddEdit({ onCancel: onModalStateChange });
  const handleFileSizeValidation = (file: File) => {
    if (file.size > MAX_FILE_SIZE) {
      form.setFields([
        {
          name: 'priselist',
          errors: [`File exceeds the maximum size of 5 MB.`],
          value: [],
        },
      ]);
      setIsSaveBtnDisabled(true);
      return Upload.LIST_IGNORE; // Prevent adding the file
    } else {
      form.setFields([
        {
          name: 'priselist',
          errors: undefined,
          value: [file],
        },
      ]);
      setIsSaveBtnDisabled(false);
    }
    return false;
  };

  return (
    <>
      {contextHolder}
      <FormModal
        form={form}
        width={400}
        title={`Add Pricelist`}
        okText={'Add'}
        disableOkBtn={isSaveBtnDisabled}
        open={openModal}
        isLoading={isLoading}
        onCancel={() => {
          form.resetFields();
          onModalStateChange();
        }}
        onSubmit={handleSubmit}
      >
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Form.Item
              name="priselist"
              label="Priselist*"
              rules={[
                {
                  required: true,
                  validator: (_, value) => {
                    if (value && value.length > 0) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Please upload a file.'));
                  },
                },
              ]}
              getValueFromEvent={(e) => e?.fileList}
            >
              <Dragger
                fileList={form.getFieldValue('priselist')}
                beforeUpload={handleFileSizeValidation}
                accept={ACCEPTED_FORMATS.join(',')}
                maxCount={1}
              >
                <UploadContent title={'Upload FIle'} message={'Max size: 5MB / Format: CSV, PDF'} />
              </Dragger>
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              label={`Supplier*`}
              name={'supplierId'}
              rules={[{ required: true, message: ValidationErrors.required('Supplier') }]}
            >
              <Select size={'large'} options={content} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              label={`Expiry Date*`}
              name={'expiryDate'}
              rules={[{ required: true, message: ValidationErrors.required('Expiry Date') }]}
            >
              <DatePicker size={'large'} placeholder={''} style={{ width: '100%' }} suffixIcon={<CalendarIcon />} />
            </Form.Item>
          </Col>
        </Row>
      </FormModal>
    </>
  );
});
